import { Fragment, FormEvent, useEffect, useState, type HTMLAttributes } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from 'react-hot-toast';
import { ArrowDown, ArrowLeft, ArrowUp, ExternalLink, Plus, X } from 'lucide-react';
import { licensingApi } from '@/api/licensing';
import { landingPagesApi } from '@/api/landingPages';
import QuestionEditor from '@/components/landing/QuestionEditor';
import { Badge, Button, Input, Loading } from '@/components/ui';
import { supabase } from '@/lib/supabase';
import { cn } from '@/utils/helpers';
import type {
  LandingPage,
  LandingPageQuestion,
  LandingPageSignup,
  LandingPageStatus,
  LandingPageTheme,
  LandingPageType,
} from '@/types';

type TabId = 'config' | 'questions' | 'rules' | 'signups';

type ConfigForm = {
  title: string;
  hero_headline: string;
  hero_subtext: string;
  cta_label: string;
  theme: LandingPageTheme;
  bullets: string[];
};

type RulesForm = {
  type: LandingPageType;
  trial_days: string;
  max_signups: string;
  plan_id: string;
  status: LandingPageStatus;
};

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'config', label: 'Config' },
  { id: 'questions', label: 'Questions' },
  { id: 'rules', label: 'Rules' },
  { id: 'signups', label: 'Signups' },
];

const STATUS_BADGE: Record<LandingPageStatus, 'success' | 'warning' | 'neutral'> = {
  active: 'success',
  paused: 'warning',
  closed: 'neutral',
};

const SIGNUP_STATUS_BADGE: Record<
  LandingPageSignup['status'],
  'success' | 'warning' | 'danger' | 'neutral'
> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  expired: 'neutral',
};

const SIGNUP_FILTERS: Array<{ id: 'all' | LandingPageSignup['status']; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

function formFromPage(page: LandingPage): ConfigForm {
  return {
    title: page.title ?? '',
    hero_headline: page.hero_headline ?? '',
    hero_subtext: page.hero_subtext ?? '',
    cta_label: page.cta_label ?? '',
    theme: {
      ...page.theme,
      accent: page.theme?.accent ?? '',
      logo_url: page.theme?.logo_url ?? '',
    },
    bullets: page.theme?.bullets ?? [],
  };
}

function rulesFormFromPage(page: LandingPage): RulesForm {
  return {
    type: page.type ?? 'waitlist',
    trial_days: String(page.trial_days ?? 14),
    max_signups: page.max_signups == null ? '' : String(page.max_signups),
    plan_id: page.plan_id ?? '',
    status: page.status ?? 'paused',
  };
}

function StatusBadge({ status }: { status: LandingPageStatus }) {
  return <Badge variant={STATUS_BADGE[status] ?? 'neutral'}>{status}</Badge>;
}

function SignupStatusBadge({ status }: { status: LandingPageSignup['status'] }) {
  return <Badge variant={SIGNUP_STATUS_BADGE[status] ?? 'neutral'}>{status}</Badge>;
}

function questionsEqual(a: LandingPageQuestion[], b: LandingPageQuestion[]) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function SortableQuestionItem({
  question,
  onChange,
  onDelete,
}: {
  question: LandingPageQuestion;
  onChange: (updated: LandingPageQuestion) => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: question.id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(isDragging && 'relative z-10 opacity-80')}
    >
      <QuestionEditor
        question={question}
        dragHandleProps={{ ...attributes, ...listeners } as HTMLAttributes<HTMLElement>}
        onChange={onChange}
        onDelete={onDelete}
      />
    </div>
  );
}

function QuestionsTab({ page }: { page: LandingPage }) {
  const queryClient = useQueryClient();
  const [localQuestions, setLocalQuestions] = useState<LandingPageQuestion[]>(page.questions ?? []);
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const savedQuestions = page.questions ?? [];
  const hasUnsavedChanges = !questionsEqual(localQuestions, savedQuestions);

  useEffect(() => {
    setLocalQuestions(page.questions ?? []);
  }, [page.id, page.questions]);

  const addQuestion = () => {
    setLocalQuestions((questions) => [
      ...questions,
      {
        id: crypto.randomUUID(),
        label: '',
        type: 'short_text',
        required: false,
      },
    ]);
  };

  const updateQuestion = (updated: LandingPageQuestion) => {
    setLocalQuestions((questions) =>
      questions.map((question) => (question.id === updated.id ? updated : question))
    );
  };

  const deleteQuestion = (questionId: string) => {
    setLocalQuestions((questions) => questions.filter((question) => question.id !== questionId));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setLocalQuestions((questions) => {
      const oldIndex = questions.findIndex((question) => question.id === active.id);
      const newIndex = questions.findIndex((question) => question.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return questions;
      return arrayMove(questions, oldIndex, newIndex);
    });
  };

  const saveQuestions = async () => {
    setSaving(true);
    try {
      await landingPagesApi.update(page.id, { questions: localQuestions });
      queryClient.setQueryData(['landing-page', page.id], (old: LandingPage | undefined) => {
        if (!old) return old;
        return { ...old, questions: localQuestions };
      });
      toast.success('Questions saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save questions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card max-w-3xl p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-ink-primary">Questions</h2>
          <Badge variant="neutral">{localQuestions.length}</Badge>
        </div>
        <Button type="button" size="sm" onClick={addQuestion}>
          <Plus className="h-4 w-4" />
          Add question
        </Button>
      </div>

      {localQuestions.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-navy-border py-12 text-center">
          <p className="mb-4 text-sm text-ink-muted">No questions yet. Add your first question.</p>
          <Button type="button" size="sm" onClick={addQuestion}>
            <Plus className="h-4 w-4" />
            Add question
          </Button>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={localQuestions.map((question) => question.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {localQuestions.map((question) => (
                <SortableQuestionItem
                  key={question.id}
                  question={question}
                  onChange={updateQuestion}
                  onDelete={() => deleteQuestion(question.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div className="mt-6 flex items-center justify-end gap-3">
        {hasUnsavedChanges && (
          <span className="inline-flex items-center gap-2 text-xs text-amber-400">
            <span className="h-2 w-2 rounded-full bg-amber-400" />
            unsaved
          </span>
        )}
        <Button type="button" onClick={() => void saveQuestions()} loading={saving}>
          Save
        </Button>
      </div>
    </div>
  );
}

function RulesTab({ page }: { page: LandingPage }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<RulesForm>(() => rulesFormFromPage(page));

  const { data: plans = [] } = useQuery({
    queryKey: ['system-plans'],
    queryFn: () => licensingApi.listAllPlans(),
  });

  useEffect(() => {
    setForm(rulesFormFromPage(page));
  }, [page]);

  const jumpstartPlans = plans.filter((plan) => plan.system_code === 'jumpstart');
  const savedForm = rulesFormFromPage(page);
  const hasUnsavedChanges = JSON.stringify(form) !== JSON.stringify(savedForm);
  const trialDays = Number(form.trial_days);
  const maxSignups = Number(form.max_signups);
  const trialDaysError =
    !Number.isInteger(trialDays) || trialDays < 1 || trialDays > 365
      ? 'Trial length must be between 1 and 365 days.'
      : '';
  const maxSignupsError =
    form.type === 'limited' &&
    (!Number.isInteger(maxSignups) || maxSignups < 1)
      ? 'Signup cap must be at least 1.'
      : '';
  const hasErrors = Boolean(trialDaysError || maxSignupsError);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (hasErrors) throw new Error('Fix validation errors before saving');
      const payload = {
        type: form.type,
        trial_days: trialDays,
        max_signups: form.type === 'limited' ? maxSignups : null,
        plan_id: form.plan_id || null,
        status: form.status,
      };
      return landingPagesApi.update(page.id, payload as Partial<LandingPage>);
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
      queryClient.setQueryData(['landing-page', page.id], updated);
      toast.success('Rules saved');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to save rules');
    },
  });

  const saveRules = () => {
    if (saveMutation.isPending || hasErrors) return;
    saveMutation.mutate();
  };

  return (
    <div className="card max-w-3xl space-y-6 p-6">
      <div>
        <h2 className="text-lg font-semibold text-ink-primary">Rules</h2>
        <p className="mt-1 text-sm text-ink-muted">Control signup behavior and trial provisioning.</p>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-ink-secondary">Page type</label>
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            {
              value: 'waitlist' as const,
              title: 'Waitlist',
              description: 'Collect signups, manually approve',
            },
            {
              value: 'limited' as const,
              title: 'Limited free signup',
              description: 'Auto-approve up to N signups, then close',
            },
          ].map((option) => {
            const selected = form.type === option.value;
            return (
              <label
                key={option.value}
                className={cn(
                  'flex cursor-pointer gap-3 rounded border p-4 transition-colors',
                  selected ? 'border-indigo-500 bg-indigo-500/10' : 'border-navy-border'
                )}
              >
                <input
                  type="radio"
                  name="landing-page-type"
                  value={option.value}
                  checked={selected}
                  onChange={() => setForm({ ...form, type: option.value })}
                  className="mt-1 h-4 w-4 accent-indigo-500"
                />
                <span>
                  <span className="block text-sm font-semibold text-ink-primary">{option.title}</span>
                  <span className="mt-1 block text-sm text-ink-muted">{option.description}</span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Input
          label="Trial length (days)"
          type="number"
          min={1}
          max={365}
          value={form.trial_days}
          error={trialDaysError}
          onChange={(event) => setForm({ ...form, trial_days: event.target.value })}
        />

        {form.type === 'limited' && (
          <Input
            label="Signup cap"
            type="number"
            min={1}
            value={form.max_signups}
            error={maxSignupsError}
            onChange={(event) => setForm({ ...form, max_signups: event.target.value })}
          />
        )}
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="target-plan" className="mb-1.5 block text-sm font-medium text-ink-secondary">
            Target plan
          </label>
          <select
            id="target-plan"
            value={form.plan_id}
            onChange={(event) => setForm({ ...form, plan_id: event.target.value })}
            className="h-10 w-full rounded border border-navy-border bg-navy-deeper px-3 text-sm text-ink-primary focus:outline-none focus:ring-2 focus:ring-gold/40"
          >
            <option value="">None / unset</option>
            {jumpstartPlans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="page-status" className="mb-1.5 block text-sm font-medium text-ink-secondary">
            Page status
          </label>
          <select
            id="page-status"
            value={form.status}
            onChange={(event) =>
              setForm({ ...form, status: event.target.value as LandingPageStatus })
            }
            className="h-10 w-full rounded border border-navy-border bg-navy-deeper px-3 text-sm text-ink-primary focus:outline-none focus:ring-2 focus:ring-gold/40"
          >
            <option value="active">Active - page accepts signups</option>
            <option value="paused">Paused - page visible but form disabled</option>
            <option value="closed">Closed - page shows closed message</option>
          </select>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        {hasUnsavedChanges && (
          <span className="inline-flex items-center gap-2 text-xs text-amber-400">
            <span className="h-2 w-2 rounded-full bg-amber-400" />
            (unsaved)
          </span>
        )}
        <Button
          type="button"
          onClick={saveRules}
          loading={saveMutation.isPending}
          disabled={hasErrors}
        >
          Save rules
        </Button>
      </div>
    </div>
  );
}

function formatSignupDate(date: string) {
  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(date));
}

function formatAnswer(value: LandingPageSignup['answers'][string]) {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function SignupsTab({ page }: { page: LandingPage }) {
  const [filter, setFilter] = useState<'all' | LandingPageSignup['status']>('all');
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: signups = [], isLoading, refetch } = useQuery({
    queryKey: ['landing-signups', page.id],
    queryFn: () => landingPagesApi.listSignups(page.id),
  });

  const filtered =
    filter === 'all' ? signups : signups.filter((signup) => signup.status === filter);
  const questionMap = Object.fromEntries((page.questions ?? []).map((question) => [question.id, question.label]));

  const approveSignup = async (signup: LandingPageSignup) => {
    setApprovingId(signup.id);
    try {
      await landingPagesApi.approveSignup(signup.id);
      toast.success('Approved');
      await refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to approve');
    } finally {
      setApprovingId(null);
    }
  };

  const rejectSignup = async (signup: LandingPageSignup) => {
    try {
      await landingPagesApi.rejectSignup(signup.id, rejectReason.trim() || undefined);
      toast.success('Rejected');
      setRejectingId(null);
      setRejectReason('');
      await refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to reject');
    }
  };

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-navy-border p-6">
        <div>
          <h2 className="text-lg font-semibold text-ink-primary">Signups</h2>
          <p className="mt-1 text-sm text-ink-muted">Review submissions and approve trials.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {SIGNUP_FILTERS.map((item) => {
            const selected = filter === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setFilter(item.id)}
                className={cn(
                  'rounded border px-3 py-1.5 text-sm font-medium transition-colors',
                  selected
                    ? 'border-gold bg-gold/10 text-gold'
                    : 'border-navy-border text-ink-secondary hover:bg-navy-hover hover:text-ink-primary'
                )}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      {isLoading ? (
        <Loading text="Loading signups..." />
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-ink-muted">No signups yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="bg-navy-deeper text-xs uppercase tracking-wide text-ink-muted">
              <tr>
                <th className="px-6 py-3 font-medium">Name</th>
                <th className="px-6 py-3 font-medium">Email</th>
                <th className="px-6 py-3 font-medium">Phone</th>
                <th className="px-6 py-3 font-medium">Submitted</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((signup) => {
                const answers = Object.entries(signup.answers ?? {});
                const isExpanded = expandedId === signup.id;
                const isRejecting = rejectingId === signup.id;

                return (
                  <Fragment key={signup.id}>
                    <tr className="border-b border-navy-border align-top">
                      <td className="px-6 py-4">
                        <div className="font-medium text-ink-primary">{signup.full_name}</div>
                        {answers.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setExpandedId(isExpanded ? null : signup.id)}
                            className="mt-2 text-xs font-medium text-gold hover:text-gold-light"
                          >
                            {isExpanded ? 'Hide answers' : 'View answers'}
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 text-ink-secondary">{signup.email}</td>
                      <td className="px-6 py-4 text-ink-secondary num">{signup.phone ?? '—'}</td>
                      <td className="px-6 py-4 text-ink-secondary">
                        {formatSignupDate(signup.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <SignupStatusBadge status={signup.status} />
                      </td>
                      <td className="px-6 py-4">
                        {signup.status === 'pending' ? (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => void approveSignup(signup)}
                              loading={approvingId === signup.id}
                              disabled={Boolean(approvingId)}
                            >
                              Approve
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="danger"
                              onClick={() => {
                                setRejectingId(signup.id);
                                setRejectReason('');
                              }}
                            >
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <span className="text-sm text-ink-muted">-</span>
                        )}
                      </td>
                    </tr>

                    {(isExpanded || isRejecting) && (
                      <tr className="border-b border-navy-border bg-navy-deeper/40">
                        <td colSpan={6} className="px-6 py-4">
                          {isExpanded && answers.length > 0 && (
                            <dl className="grid gap-3 sm:grid-cols-2">
                              {answers.map(([key, value]) => (
                                <div key={key}>
                                  <dt className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                                    {questionMap[key] ?? key}
                                  </dt>
                                  <dd className="mt-1 text-sm text-ink-primary">{formatAnswer(value)}</dd>
                                </div>
                              ))}
                            </dl>
                          )}

                          {isRejecting && (
                            <div className={cn(isExpanded && answers.length > 0 && 'mt-4 border-t border-navy-border pt-4')}>
                              <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
                                <Input
                                  label="Reason (optional)"
                                  value={rejectReason}
                                  onChange={(event) => setRejectReason(event.target.value)}
                                />
                                <Button
                                  type="button"
                                  variant="danger"
                                  onClick={() => void rejectSignup(signup)}
                                >
                                  Confirm reject
                                </Button>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  onClick={() => {
                                    setRejectingId(null);
                                    setRejectReason('');
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function LandingPageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('config');
  const [form, setForm] = useState<ConfigForm | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const { data: page, isLoading } = useQuery({
    queryKey: ['landing-page', id],
    queryFn: () => landingPagesApi.get(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (page) setForm(formFromPage(page));
  }, [page]);

  const saveMutation = useMutation({
    mutationFn: async (nextForm: ConfigForm) => {
      if (!id) throw new Error('Landing page id is missing');
      return landingPagesApi.update(id, {
        title: nextForm.title.trim(),
        hero_headline: nextForm.hero_headline.trim() || undefined,
        hero_subtext: nextForm.hero_subtext.trim() || undefined,
        cta_label: nextForm.cta_label.trim() || undefined,
        theme: {
          ...nextForm.theme,
          bullets: nextForm.bullets.filter((bullet) => bullet.trim() !== ''),
        },
      });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(['landing-page', id], updated);
      queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
      toast.success('Saved');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to save');
    },
  });

  const save = () => {
    if (!form || saveMutation.isPending) return;
    saveMutation.mutate(form);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    save();
  };

  if (isLoading) return <Loading text="Loading landing page..." />;
  if (!page || !form) {
    return <div className="py-20 text-center text-ink-secondary">Landing page not found</div>;
  }

  return (
    <div dir="ltr">
      <div className="mb-6 flex items-center gap-2 text-sm text-ink-muted">
        <Link to="/landing-pages" className="flex items-center gap-1 transition-colors hover:text-gold">
          <ArrowLeft className="h-3.5 w-3.5" />
          Landing Pages
        </Link>
        <span>/</span>
        <span className="text-ink-primary">{page.title}</span>
      </div>

      <div className="mb-8 flex flex-wrap items-start gap-4">
        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 text-xl font-black text-indigo-300">
          {page.title.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-ink-primary">{page.title}</h1>
            <StatusBadge status={page.status} />
          </div>
          <p className="mt-1 font-mono text-sm text-ink-secondary">{page.slug}</p>
        </div>
        <a
          href={`https://join.jumpstart.co.il/join/${page.slug}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex h-10 items-center gap-2 rounded border border-navy-border bg-navy-card px-4 text-sm font-medium text-ink-primary transition-colors hover:bg-navy-hover"
        >
          View public page
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-navy-border">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              '-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'border-gold text-gold'
                : 'border-transparent text-ink-secondary hover:text-ink-primary'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'config' && (
        <form onSubmit={handleSubmit} className="card max-w-3xl space-y-5 p-6">
          <Input
            label="Title"
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
            onBlur={save}
            required
          />

          <Input
            label="Hero headline"
            value={form.hero_headline}
            onChange={(event) => setForm({ ...form, hero_headline: event.target.value })}
            onBlur={save}
          />

          <div>
            <label htmlFor="hero-subtext" className="mb-1.5 block text-sm font-medium text-ink-secondary">
              Hero subtext
            </label>
            <textarea
              id="hero-subtext"
              value={form.hero_subtext}
              onChange={(event) => setForm({ ...form, hero_subtext: event.target.value })}
              onBlur={save}
              rows={2}
              className="w-full rounded border border-navy-border bg-navy-deeper px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40"
            />
          </div>

          <Input
            label="CTA label"
            value={form.cta_label}
            onChange={(event) => setForm({ ...form, cta_label: event.target.value })}
            onBlur={save}
            placeholder="Join the waitlist"
          />

          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-secondary">
              Accent color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={/^#[0-9a-f]{6}$/i.test(form.theme.accent ?? '') ? form.theme.accent : '#6366f1'}
                onChange={(event) =>
                  setForm({ ...form, theme: { ...form.theme, accent: event.target.value } })
                }
                onBlur={save}
                className="h-10 w-12 cursor-pointer rounded border border-navy-border bg-navy-deeper p-1"
                title="Pick accent color"
              />
              <input
                type="text"
                value={form.theme.accent ?? ''}
                onChange={(event) =>
                  setForm({ ...form, theme: { ...form.theme, accent: event.target.value } })
                }
                onBlur={save}
                placeholder="#6366f1"
                className="h-10 flex-1 rounded border border-navy-border bg-navy-deeper px-3 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-secondary">
              Logo
            </label>
            <div className="flex items-start gap-3">
              {form.theme.logo_url && (
                <img
                  src={form.theme.logo_url}
                  alt="Logo preview"
                  className="h-10 max-w-[120px] rounded border border-navy-border bg-white object-contain p-1"
                />
              )}
              <div className="flex-1 space-y-2">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-navy-border bg-navy-deeper px-3 py-2 text-sm text-ink-secondary transition-colors hover:bg-navy-hover hover:text-ink-primary">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    className="sr-only"
                    disabled={logoUploading}
                    onChange={async (event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      if (!id) {
                        toast.error('Landing page id is missing');
                        return;
                      }

                      setLogoUploading(true);
                      try {
                        const { data: { session } } = await supabase.auth.getSession();
                        if (!session?.access_token) throw new Error('You must be signed in to upload a logo');

                        const formData = new FormData();
                        formData.append('file', file);
                        const res = await supabase.functions.invoke('upload-landing-logo', {
                          body: formData,
                          headers: { Authorization: `Bearer ${session.access_token}` },
                        });

                        if (res.error) throw res.error;
                        const url = (res.data as { url?: string }).url;
                        if (!url) throw new Error('Upload did not return a logo URL');

                        const next = { ...form, theme: { ...form.theme, logo_url: url } };
                        setForm(next);
                        const updated = await landingPagesApi.update(id, { theme: next.theme });
                        queryClient.setQueryData(['landing-page', id], updated);
                        queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
                        toast.success('Logo uploaded');
                      } catch (error) {
                        toast.error(error instanceof Error ? error.message : 'Upload failed');
                      } finally {
                        setLogoUploading(false);
                        event.target.value = '';
                      }
                    }}
                  />
                  {logoUploading ? 'Uploading...' : 'Upload image'}
                </label>
                {form.theme.logo_url && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!id) {
                        toast.error('Landing page id is missing');
                        return;
                      }

                      const next = { ...form, theme: { ...form.theme, logo_url: '' } };
                      setForm(next);
                      try {
                        const updated = await landingPagesApi.update(id, { theme: next.theme });
                        queryClient.setQueryData(['landing-page', id], updated);
                        queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
                      } catch (error) {
                        toast.error(error instanceof Error ? error.message : 'Failed to remove logo');
                      }
                    }}
                    className="block text-xs text-ink-muted hover:text-red-400"
                  >
                    Remove logo
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="mb-2 text-sm font-medium text-ink-secondary">
              Feature bullets <span className="text-xs text-ink-muted">(shown in hero section)</span>
            </div>
            <div className="space-y-2">
              {form.bullets.map((bullet, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    value={bullet}
                    onChange={(event) => {
                      const next = form.bullets.map((item, itemIndex) =>
                        itemIndex === index ? event.target.value : item
                      );
                      setForm({ ...form, bullets: next });
                    }}
                    onBlur={save}
                    placeholder={`Bullet ${index + 1}`}
                    className="h-9 flex-1 rounded border border-navy-border bg-navy-deeper px-3 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = [...form.bullets];
                      const [moved] = next.splice(index, 1);
                      next.splice(index - 1, 0, moved);
                      setForm({ ...form, bullets: next });
                    }}
                    disabled={index === 0}
                    className="flex h-9 w-9 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-navy-hover hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Move bullet up"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const next = [...form.bullets];
                      const [moved] = next.splice(index, 1);
                      next.splice(index + 1, 0, moved);
                      setForm({ ...form, bullets: next });
                    }}
                    disabled={index === form.bullets.length - 1}
                    className="flex h-9 w-9 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-navy-hover hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Move bullet down"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const next = form.bullets.filter((_, itemIndex) => itemIndex !== index);
                      setForm({ ...form, bullets: next });
                    }}
                    className="flex h-9 w-9 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-red-500/10 hover:text-red-400"
                    aria-label="Remove bullet"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setForm({ ...form, bullets: [...form.bullets, ''] })}
                className="inline-flex h-9 items-center gap-2 rounded border border-navy-border bg-navy-deeper px-3 text-sm text-ink-secondary transition-colors hover:bg-navy-hover hover:text-ink-primary"
              >
                <Plus className="h-4 w-4" />
                Add bullet
              </button>
            </div>
          </div>

          {/* Thank you page */}
          <div>
            <div className="mb-2 text-sm font-medium text-ink-secondary">
              Thank you page
            </div>
            <div className="space-y-3">
              <Input
                label="Heading"
                value={form.theme.thank_you_title ?? ''}
                onChange={(event) =>
                  setForm({ ...form, theme: { ...form.theme, thank_you_title: event.target.value } })
                }
                onBlur={save}
                placeholder="You're on the list!"
              />
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink-secondary">Message</label>
                <textarea
                  value={form.theme.thank_you_message ?? ''}
                  onChange={(event) =>
                    setForm({ ...form, theme: { ...form.theme, thank_you_message: event.target.value } })
                  }
                  onBlur={save}
                  rows={2}
                  placeholder="We'll review your application and be in touch soon."
                  className="w-full rounded border border-navy-border bg-navy-deeper px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end">
            <Button type="submit" loading={saveMutation.isPending}>
              Save
            </Button>
          </div>
        </form>
      )}

      {activeTab === 'questions' && (
        <QuestionsTab page={page} />
      )}
      {activeTab === 'rules' && (
        <RulesTab page={page} />
      )}
      {activeTab === 'signups' && (
        <SignupsTab page={page} />
      )}
    </div>
  );
}
